import { createContext, useContext, type ReactNode } from 'react';
import { ArrowRight, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { InfoTooltip } from '@/components/ui/info-tooltip';
import { Skeleton } from '@/components/ui/skeleton';
import { FlechaMonto } from '@/components/ui/monto-tendencia';
import { CLASE_TONO_MONTO, prefijoLecturaMonto, sentidoDeMonto, type SentidoMonto } from '@/lib/montoSentido';

/**
 * Tarjeta de KPI del proyecto: label arriba, valor grande y sublabel.
 *
 * Vivia en `admin/portal-cobranza/CollectionStatCard`, asi que cualquier otro
 * portal que la usara importaba desde dentro de un portal ajeno. Es una pieza
 * de UI sin dominio: su lugar es `components/ui`, y ahi la consumen cobranza,
 * Legal Flow y quien siga.
 *
 * - Sin `onClick` -> tarjeta de solo lectura (div).
 * - Con `onClick` -> tarjeta clickeable (button) con flecha de drill.
 * - `variant` 'money' (monto en una linea) | 'count' (numero grande).
 * - `info` -> icono de informacion junto al label con la explicacion en un tooltip.
 * - `icon` + `iconClass` -> insignia tintada (lucide) junto al label, con el mismo lenguaje que
 *   `detail-field`/`action-card` (chip `rounded-md bg-<tono>/10 text-<tono>`). Opcional y
 *   retrocompatible: sin `icon` la tarjeta se ve exactamente igual que antes.
 * - `children` -> contenido extra debajo del sublabel (desglose, barra, etc.).
 * - `sentido` ('ingreso' | 'adeudo' | 'neutro') -> flecha de tendencia junto al valor y su color
 *   (verde arriba lo que entra, rojo abajo lo que se debe; 0 en gris sin flecha). Decide por
 *   `monto` o, si no viene, por `value` cuando es numero. Ese color gana sobre `valueClass`.
 *
 * Modo uniforme: dentro de `<KpiCardUniforme>` (o con `uniforme`) todas las tarjetas
 * miden igual sin importar el `variant`: mismo padding, mismo alto en la fila y la
 * misma tipografia de valor y label. Lo activa un portal completo desde su layout
 * (Portal de Cobranza, 2026-10-05: "las tarjetas de Unidades son mas grandes que las
 * de Cuentas de Cobranza"). Los demas portales quedan igual.
 */
const KpiCardUniformeContext = createContext(false);

export function KpiCardUniforme({ children }: { children: ReactNode }) {
  return <KpiCardUniformeContext.Provider value={true}>{children}</KpiCardUniformeContext.Provider>;
}

export interface KpiCardProps {
  label: string;
  value: React.ReactNode;          // ya formateado (ej. formatCurrency(x)) o un número
  sublabel?: React.ReactNode;
  labelClass?: string;             // color del label (text-danger/success/warning/primary/muted…)
  valueClass?: string;             // color del valor
  sublabelClass?: string;          // color del sublabel (default muted)
  variant?: 'money' | 'count';
  onClick?: () => void;            // si viene, la card es clickeable (link)
  arrowClass?: string;             // color del hover de la flecha
  /** Explicacion del KPI: icono de informacion junto al label, con tooltip. */
  info?: React.ReactNode;
  /** Icono (lucide) para la insignia tintada junto al label; sin el, la tarjeta queda igual que antes. */
  icon?: LucideIcon;
  /** Color de la insignia del icono (fondo + texto), p.ej. "bg-primary/10 text-primary". Default: muted. */
  iconClass?: string;
  /** Contenido extra debajo del sublabel. */
  children?: React.ReactNode;
  /** Mientras carga: skeleton en el valor y el sublabel (nunca un texto "Calculando"). */
  loading?: boolean;
  /** Sentido del monto: flecha de tendencia y color del valor (ver `lib/montoSentido`). */
  sentido?: SentidoMonto;
  /** Monto crudo para decidir la flecha cuando `value` ya viene formateado. */
  monto?: number | null;
  /** Desde cuántos centavos se marca el sentido (cobranza avisa desde $1 = 100). */
  umbralCentavos?: number;
  /** Fuerza (true) o apaga (false) el modo uniforme; si no viene, lo decide el contexto. */
  uniforme?: boolean;
  className?: string;
}

export function KpiCard({
  label,
  value,
  sublabel,
  labelClass = 'text-muted-foreground',
  valueClass = 'text-foreground',
  sublabelClass = 'text-muted-foreground',
  variant = 'money',
  onClick,
  arrowClass = 'group-hover:text-primary',
  info,
  icon: Icon,
  iconClass,
  children,
  loading = false,
  sentido,
  monto,
  umbralCentavos,
  uniforme,
  className,
}: KpiCardProps) {
  const ctxUniforme = useContext(KpiCardUniformeContext);
  const esUniforme = uniforme ?? ctxUniforme;
  const montoCrudo = monto !== undefined ? monto : typeof value === 'number' ? value : null;
  const estilo = sentido ? sentidoDeMonto(montoCrudo, sentido, umbralCentavos) : null;
  const prefijo = estilo ? prefijoLecturaMonto(estilo) : '';

  const valueCls = cn(
    'font-bold tabular-nums mb-1.5',
    esUniforme
      ? 'text-[17px] leading-tight sm:text-[18px] break-words'
      : variant === 'count' ? 'text-[32px] leading-none' : 'text-[15px] leading-tight sm:text-[17px] break-words',
    valueClass,
    estilo && estilo.tono !== 'neutro' && estilo.tono !== 'vacio' && CLASE_TONO_MONTO[estilo.tono],
  );

  const labelNode = (
    <span className={cn('text-[10px] font-semibold uppercase tracking-wider inline-flex items-center gap-1 min-w-0', labelClass)}>
      {label}
      {info && <InfoTooltip content={info} ariaLabel={`Qué significa ${label}`} />}
    </span>
  );

  const body = (
    <>
      <div className="flex items-start justify-between gap-2 mb-3">
        {Icon ? (
          <div className="flex min-w-0 items-center gap-2">
            <span aria-hidden className={cn('flex size-8 shrink-0 items-center justify-center rounded-md', iconClass ?? 'bg-muted text-muted-foreground')}>
              <Icon className="size-4" strokeWidth={1.75} />
            </span>
            {labelNode}
          </div>
        ) : labelNode}
        {onClick && (
          <ArrowRight className={cn('w-3.5 h-3.5 text-muted-foreground/25 shrink-0 transition-colors', arrowClass)} strokeWidth={1.75} />
        )}
      </div>
      {loading ? (
        <>
          <Skeleton className={cn('mb-2', variant === 'count' && !esUniforme ? 'h-8 w-20' : 'h-5 w-32')} aria-hidden />
          {sublabel != null && sublabel !== '' && <Skeleton className="h-3 w-24" aria-hidden />}
          <span className="sr-only">Cargando {label}</span>
        </>
      ) : (
        <>
          {estilo?.flecha ? (
            <div className={cn(valueCls, 'flex items-center gap-1.5')}>
              <FlechaMonto estilo={estilo} size="lg" />
              {prefijo && <span className="sr-only">{prefijo} </span>}
              <span className="min-w-0">{value}</span>
            </div>
          ) : (
            <div className={valueCls}>{value}</div>
          )}
          {sublabel != null && sublabel !== '' && (
            <p className={cn('text-[11px] leading-snug', sublabelClass)}>{sublabel}</p>
          )}
        </>
      )}
      {children}
    </>
  );

  const base = cn('sozu-kpi-card overflow-hidden', esUniforme && 'h-full flex flex-col', className);

  return onClick ? (
    <button type="button" onClick={onClick} className={cn(base, 'text-left group hover:shadow-sm transition-all duration-200')}>
      {body}
    </button>
  ) : (
    <div className={base}>{body}</div>
  );
}
